/**
 * SignUp — split-pane (mirrors SignIn). Left column: "Build a card in
 * 90 seconds" copy + sample FloatingCard. Right column: white card
 * with terms gate → OAuth buttons OR email/password form.
 *
 * Handles the pending-OAuth bounce from /auth/signin (where a returning
 * OAuth attempt hit TERMS_REQUIRED): the id_token is stashed in
 * sessionStorage; once terms are checked here, we auto-complete the
 * OAuth signup and navigate on.
 */
import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, APIError, sessionStore } from '@/lib/api';
import { friendlyError } from '@/lib/errors';
import {
  Btn, Checkable, Eyebrow, Field, FloatingCard, Icons,
} from '@/components';
import { OAuthButtons } from '@/components/OAuthButtons';
import { SignupBlockedModal } from '@/components/SignupBlockedModal';

interface PendingOAuth { provider: 'google' | 'apple'; id_token: string }

export function SignUpPage() {
  const navigate = useNavigate();
  const [view, setView] = useState<'oauth' | 'email'>('oauth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [terms, setTerms] = useState(false);
  const [updates, setUpdates] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingOAuth, setPendingOAuth] = useState<PendingOAuth | null>(null);
  // Triggered on backend SIGNUPS_DISABLED — switches to "we're not
  // open yet" + waitlist capture instead of an error toast.
  const [blockedOpen, setBlockedOpen] = useState(false);

  // Password strength — same 4-rule shape the design's signup-email
  // screen uses (length + digit + mixed case + symbol).
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const pwLen = password.length >= 8;
  const pwNum = /\d/.test(password);
  const pwMix = /[a-z]/.test(password) && /[A-Z]/.test(password);
  const pwSym = /[^A-Za-z0-9]/.test(password);
  const ruleCount = [pwLen, pwNum, pwMix, pwSym].filter(Boolean).length;
  const canSubmit = emailValid && pwLen && pwNum && terms;

  useEffect(() => {
    const raw = sessionStorage.getItem('pending_oauth');
    if (raw) {
      try { setPendingOAuth(JSON.parse(raw)); } catch { sessionStorage.removeItem('pending_oauth'); }
    }
  }, []);

  useEffect(() => {
    if (!pendingOAuth || !terms) return;
    let cancelled = false;
    (async () => {
      const fn = pendingOAuth.provider === 'google' ? api.oauthGoogle : api.oauthApple;
      try {
        const pair = await fn({ id_token: pendingOAuth.id_token, terms_accepted: true });
        if (cancelled) return;
        sessionStorage.removeItem('pending_oauth');
        sessionStore.set(pair.access, pair.refresh, pair.user);
        navigate(pair.user.profile_complete ? '/home' : '/builder');
      } catch (err) {
        if (cancelled) return;
        if (err instanceof APIError && err.code === 'SIGNUPS_DISABLED') {
          sessionStorage.removeItem('pending_oauth');
          setBlockedOpen(true);
          return;
        }
        setError(friendlyError(err, 'oauthSignup'));
      }
    })();
    return () => { cancelled = true; };
  }, [pendingOAuth, terms, navigate]);

  const submitEmail = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const pair = await api.signup({ email, password, terms_accepted: terms });
      sessionStore.set(pair.access, pair.refresh, pair.user);
      navigate('/builder');
    } catch (err) {
      if (err instanceof APIError && err.code === 'SIGNUPS_DISABLED') {
        setBlockedOpen(true);
      } else {
        setError(friendlyError(err, 'signup'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="auth-split"
      style={{
        maxWidth: 1280,
        margin: '0 auto',
        padding: '60px 40px',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 80,
        alignItems: 'center',
        minHeight: 'calc(100vh - 200px)',
      }}
    >
      {/* LEFT — copy + sample card */}
      <div>
        <Eyebrow tone="coral" letter={3} style={{ marginBottom: 18 }}>Get started</Eyebrow>
        <h1
          className="serif auth-headline"
          style={{
            fontSize: 64,
            fontWeight: 500,
            letterSpacing: -1.8,
            lineHeight: 1.02,
            margin: 0,
            textWrap: 'balance' as const,
          }}
        >
          Build a card <span style={{ fontStyle: 'italic' }}>in 90 seconds.</span>
        </h1>
        <p style={{ fontSize: 17, color: 'var(--ink-2)', lineHeight: 1.5, marginTop: 20, maxWidth: 460 }}>
          A short branded link, anonymous feedback, and a clear score. No swiping. No signup required for raters.
        </p>
        <div className="auth-sample-card" style={{ marginTop: 36, position: 'relative', width: 280, height: 220 }}>
          <FloatingCard rotate={-4} top={0} left={0} z={1} variant={1} initial="M" name="You" score={7.8} primary />
        </div>
      </div>

      {/* RIGHT — auth card */}
      <div style={{ maxWidth: 440, justifySelf: 'center', width: '100%' }}>
        <div className="auth-card" style={{ background: 'var(--surface)', borderRadius: 24, padding: 32, boxShadow: 'var(--shadow-lift)' }}>
          <div className="serif" style={{ fontSize: 28, fontWeight: 500, letterSpacing: -0.5 }}>Sign up</div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 4 }}>
            pick one — you can always link more later
          </div>

          {pendingOAuth ? (
            /* Finish the OAuth bounce-back from /auth/signin */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18, marginTop: 24 }}>
              <Checkable checked={terms} onChange={() => setTerms((t) => !t)}>
                I agree to the <Link to="/terms"><u>Terms</u></Link> and{' '}
                <Link to="/privacy"><u>Privacy Policy</u></Link>.
              </Checkable>
              {error && <ErrorBox>{error}</ErrorBox>}
              <p style={{ fontSize: 13, color: 'var(--ink-3)', textAlign: 'center', margin: 0 }}>
                {terms
                  ? 'Finishing your sign-up…'
                  : `Accept the terms to finish signing up with ${pendingOAuth.provider}.`}
              </p>
            </div>
          ) : view === 'oauth' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18, marginTop: 24 }}>
              <Checkable checked={terms} onChange={() => setTerms((t) => !t)}>
                I agree to the <Link to="/terms"><u>Terms</u></Link> and{' '}
                <Link to="/privacy"><u>Privacy Policy</u></Link>.
              </Checkable>
              <OAuthButtons mode="signup" termsAccepted={terms} />
              <Divider />
              <Btn
                variant="outline"
                size="lg"
                full
                onClick={() => { setView('email'); setError(null); }}
              >
                Sign up with email
              </Btn>
              {error && <ErrorBox>{error}</ErrorBox>}
            </div>
          ) : (
            <form onSubmit={submitEmail} style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 24 }}>
              <Field label="Email">
                <input
                  type="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@somewhere.com"
                  autoFocus
                  style={{
                    ...inputStyle,
                    borderColor: email && !emailValid ? '#d04030' : emailValid ? 'var(--coral)' : 'transparent',
                  }}
                />
              </Field>

              <Field label="Password">
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={showPass ? 'Make it memorable' : '• • • • • • • •'}
                    style={{ ...inputStyle, paddingRight: 46 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass((s) => !s)}
                    style={{
                      position: 'absolute',
                      right: 8,
                      top: 7,
                      padding: 8,
                      color: 'var(--ink-3)',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                    aria-label={showPass ? 'Hide password' : 'Show password'}
                  >
                    <Icons.eye size={16} />
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 4, marginTop: 10 }}>
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      style={{
                        flex: 1,
                        height: 3,
                        borderRadius: 3,
                        background: i <= ruleCount ? 'var(--coral)' : 'var(--line-strong)',
                        opacity: i <= ruleCount ? 1 : 0.35,
                        transition: 'all 0.2s',
                      }}
                    />
                  ))}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 10 }}>
                  {([['8+ chars', pwLen], ['a number', pwNum], ['UPPER + lower', pwMix], ['a symbol', pwSym]] as const).map(([t, met]) => (
                    <RuleChip key={t} met={met}>{t}</RuleChip>
                  ))}
                </div>
              </Field>

              <Checkable checked={terms} onChange={() => setTerms((t) => !t)}>
                I agree to the <Link to="/terms"><u>Terms</u></Link> and{' '}
                <Link to="/privacy"><u>Privacy Policy</u></Link>.
              </Checkable>
              <Checkable checked={updates} onChange={() => setUpdates((u) => !u)} muted>
                Send me product updates. Honest cadence — never more than monthly.
              </Checkable>

              {error && <ErrorBox>{error}</ErrorBox>}

              <Btn variant="primary" size="lg" full type="submit" disabled={!canSubmit} loading={submitting}>
                {submitting ? 'Creating account…' : 'Create account'}
              </Btn>
              <div style={{ textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={() => { setView('oauth'); setError(null); }}
                  style={{ background: 'transparent', border: 'none', color: 'var(--coral)', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
                >
                  ← Sign up with Google or Apple
                </button>
              </div>
            </form>
          )}

          <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--ink-3)', marginTop: 22, lineHeight: 1.5 }}>
            Already have an account?{' '}
            <Link to="/auth/signin" style={{ color: 'var(--ink)', fontWeight: 600, textDecoration: 'underline' }}>
              Sign in
            </Link>
          </div>
        </div>
        <div style={{ marginTop: 16, textAlign: 'center', fontSize: 11, color: 'var(--ink-4)', lineHeight: 1.5 }}>
          By continuing you agree to our <Link to="/terms" style={{ color: 'inherit' }}><u>Terms</u></Link> and{' '}
          <Link to="/privacy" style={{ color: 'inherit' }}><u>Privacy Policy</u></Link>.
        </div>
      </div>

      <SignupBlockedModal
        open={blockedOpen}
        prefillEmail={email}
        onClose={() => setBlockedOpen(false)}
      />
    </div>
  );
}

function Divider() {
  return (
    <div
      className="mono"
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        color: 'var(--ink-4)', fontSize: 10, letterSpacing: 2, margin: '4px 0',
      }}
    >
      <div style={{ flex: 1, height: 1, background: 'var(--line-strong)' }} />
      OR
      <div style={{ flex: 1, height: 1, background: 'var(--line-strong)' }} />
    </div>
  );
}

function RuleChip({ met, children }: { met: boolean; children: React.ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '4px 9px', borderRadius: 100,
        background: met ? 'var(--coral-soft)' : 'var(--chip)',
        color: met ? 'var(--coral-ink)' : 'var(--ink-3)',
        fontSize: 11, fontWeight: 500,
      }}
    >
      {met ? <Icons.check size={10} stroke={3.2} /> : <span style={{ width: 8, height: 8, borderRadius: 8, border: '1.5px solid currentColor', opacity: 0.5 }} />}
      {children}
    </span>
  );
}

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: 'rgba(208,64,48,0.08)',
        border: '1px solid rgba(208,64,48,0.2)',
        borderRadius: 12,
        padding: '10px 14px',
        fontSize: 13,
        color: '#a8331f',
      }}
    >
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--bg)',
  borderRadius: 12,
  padding: '14px 16px',
  fontFamily: 'var(--font-ui)',
  fontSize: 15,
  color: 'var(--ink)',
  border: '1.5px solid transparent',
  outline: 'none',
};
